<template>
  <ion-button fill="clear" @click="openModal" data-cy="user-switcher">
    <ion-avatar slot="start" class="h-6 w-6 mr-1">
      <img :src="currentUser?.avatar_url" alt="User avatar" />
    </ion-avatar>
    <ion-icon :icon="caretDownOutline" size="small"></ion-icon>
  </ion-button>

  <ion-modal
    :is-open="isModalOpen"
    @didDismiss="isModalOpen = false"
    class="user-switcher-modal"
  >
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ $t('userSwitcher.switchUser') }}</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="isModalOpen = false">
            <ion-icon :icon="closeOutline" slot="icon-only"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    
    <ion-content>
      <ion-list lines="full">
        <ion-item
          v-for="user in availableUsers"
          :key="user.customer_id"
          button
          :detail="false"
          @click="selectUser(user)"
          :class="{ 'bg-primary/5': user.customer_id === currentUser?.customer_id }"
          data-cy="user-option"
        >
          <ion-avatar slot="start" class="w-12 h-12 mr-md">
            <img :src="user.avatar_url" :alt="user.display_name" />
          </ion-avatar>
          <ion-label>
            <h2 class="font-semibold text-lg mb-xs">{{ user.display_name }}</h2>
            <p class="text-text-secondary">{{ getUserSegment(user) }}</p>
          </ion-label>
          <ion-badge v-if="user.customer_id === currentUser?.customer_id" color="primary">{{ $t('common.currentUser') }}</ion-badge>
        </ion-item>
      </ion-list>
    </ion-content>
  </ion-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useUserStore } from '../stores/userStore';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { Customer } from '../types';
import {
  IonButton,
  IonIcon,
  IonModal,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
  IonBadge,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons
} from '@ionic/vue';
import { caretDownOutline, closeOutline } from 'ionicons/icons';

const userStore = useUserStore();
const router = useRouter();
const route = useRoute();
const isModalOpen = ref(false);
const i18n = useI18n();

const currentUser = computed(() => userStore.user);

// This will be populated from api/user service
const availableUsers = ref<Customer[]>([]);

onMounted(async () => {
  await loadAvailableUsers();
});

async function loadAvailableUsers() {
  const users = await userStore.getAvailableUsers();
  availableUsers.value = users;
}

function openModal() {
  isModalOpen.value = true;
}

async function selectUser(user: Customer) {
  const previousSegment = userStore.userSegment;
  await userStore.switchUser(user.customer_id);
  
  // If switching from advanced to professional segment while on the leaderboard page,
  // redirect to home since leaderboard is only for advanced segment
  if (
    previousSegment === 'advanced' && 
    userStore.userSegment === 'professional' && 
    route.path === '/leaderboard'
  ) {
    router.replace('/home');
  }
  
  isModalOpen.value = false;
}

function getUserSegment(user: Customer): string {
  // Simplified segment display
  if (user.tags?.segment === 'advanced') {
    return i18n.t('segments.advanced');
  } else if (user.tags?.segment === 'professional') {
    return i18n.t('segments.professional');
  }
  
  return i18n.t('segments.standard');
}
</script>

<style scoped>
/* Modal styling - keeping this because it uses deep selectors that are harder to replace with Tailwind */
:deep(.user-switcher-modal) {
  --width: 90%;
  --max-width: 400px;
  --height: auto;
  --max-height: 80%;
  --border-radius: var(--radius-lg);
}

/* Apply Tailwind styles for ion-item via CSS variables */
ion-item {
  --padding-start: theme('spacing.md');
  --padding-end: theme('spacing.md');
  --padding-top: theme('spacing.md');
  --padding-bottom: theme('spacing.md');
  --inner-padding-end: theme('spacing.md');
}
</style> 